import { USER_RIGHT_STATUS } from "@/constants/biz";
import {
  AUTH_RIGHT_PACKAGE_CODES,
  isTemRightPackage,
  VIP_CONFIG,
  VIP_RIGHT_IDENTIFICATION,
} from "../constants/vip";
import request from "../utils/request";
import { getBizUserRightList } from "./biz";
import { getUserRightCount } from "./material";
import { getSubscriptionList } from "./order";

export function getVipGoods(category_ids) {
  return request({
    url: "/biz/resources/goods",
    query: {
      category_ids: category_ids || VIP_CONFIG.HBCategoryId,
      page_size: 200,
    },
  });
}

export function getSubscribeActivities(activity_ids) {
  return request({
    url: "/biz/external-subscribe/activities",
    query: {
      activity_ids,
    },
    ignoreMessage: true,
  });
}

/**
 * 获取当前有效会员，
 * vips: 当前生效的会员信息
 * material_vip_expire_at：当前等级会员实际过期时间
 * 因为会存在同等级不同会员（比如：个人会员下载5个、个人会员下载10个）,
 * vips只有当前生效中的会员，未生效的会员信息不在里面，
 * 所以需要以此字段作为补充获取实际会员过期时间
 * @returns {Promise<{
 *  vips: IVip[],
 *  material_vip_expire_at: number,
 * }>}
 */
export async function getCurrentVip() {
  return request({
    url: `/biz/users/vips/composite`,
    ignoreMessage: true,
  })
    .then((result) => {
      if (result?.err || !result?.vips) {
        return {
          vips: [],
        };
      }

      return {
        ...result,
        // 这里将过期会员剔除掉
        vips: result.vips.filter((vip) => !vip.is_expired),
      };
    })
    .catch(() => ({
      vips: [],
    }));
}

/**
 * 获取会员Zone
 * @param {string} system_code 会员体系
 */
export async function getCurrentVipZone(system_code) {
  return request({
    url: `/biz/users/vip-zone`,
    query: {
      system_code,
    },
  });
}

export async function getCurrentVipSubject() {
  return request({
    url: `/biz/users/vip-subjects`,
  });
}

export async function getUpdatePrice(data) {
  return request({
    method: "post",
    url: "/biz/order/calculate",
    data,
  });
}

export function exchangeVip(code) {
  return request({
    url: `/biz/exchage/${code}/user-vips`,
    method: "post",
  });
}

export function submitCompanyVipConsult({
  name,
  mobile,
  company,
  company_scale,
  download_cnt,
  source,
  leads_entrance,
}) {
  return request({
    url: "/crm/leads/create",
    method: "post",
    data: {
      name,
      mobile,
      company,
      company_scale,
      download_cnt,
      source,
      leads_entrance,
    },
  });
}
/**
 * 潜在线索同步
 */
export function syncLead() {
  return request({ url: "/crm/leads/sync", method: "post" });
}

/**
 * @typedef {{
 * err?: number,
 * msg?: string,
 * vips: IVip[],
 * vip_expire: number | string,
 * auth_rights_expire: number | string;
 * auth_rights: IUserRight[],
 * effect_rights: IUserRight[],
 * subscription: ISubscriptionItem[],
 * downloadCount: [{
 *   left: number,
 *    quantity: number,
 *    usage: number
 * }]
 * rightPkgCodeMap: Record<string, IUserRight> 
 *}} RightData
 * 获取用户会员与权益信息
    @returns {Promise<RightData>}
 */
export async function getUserRight() {
  const [{ vips, material_vip_expire_at }, subscription] = await Promise.all([
    getCurrentVip(),
    // 获取订阅信息
    getSubscriptionList({ status: "active" }),
  ]);
  const effect_vips = vips.filter((vip) => !vip.is_expired);

  if (effect_vips.length === 0) {
    return {
      vips: [],
      tem_rights: [],
      auth_rights: [],
      effect_rights: [],
      subscription: subscription?.list || [],
      downloadCount: [],
    };
  }

  const [userRights, downloadCount] = await Promise.all([
    // 获取用户权益
    getBizUserRightList({
      status: USER_RIGHT_STATUS.NORMAL,
      page_size: 100,
      not_expired: 1,
    }),
    // 获取下载次数信息
    getUserRightCount([{ right_code: VIP_RIGHT_IDENTIFICATION }]),
  ]);

  const rights = userRights.list || [];

  // 生效权益
  const effect_rights = rights.filter((item) => item.status === 1);

  // 权益包code与最新的权益映射，用于计算买权益的时候，权益起始时间
  const rightPkgCodeMap = {};
  effect_rights.forEach((right) => {
    const preRight = rightPkgCodeMap[right.right_package_code];
    if (preRight) {
      rightPkgCodeMap[right.right_package_code] =
        new Date(preRight.end_at).getTime() > new Date(right.end_at).getTime()
          ? preRight
          : right;
    } else {
      rightPkgCodeMap[right.right_package_code] = right;
    }
  });

  // 升级包权益
  const tem_rights = effect_rights.filter((item) =>
    isTemRightPackage(item.right_package_code)
  );

  // 升级包权益
  const auth_rights = rights.filter((item) =>
    AUTH_RIGHT_PACKAGE_CODES.includes(item.right_package_code)
  );
  const auth_rights_expire =
    auth_rights.length &&
    Math.max(...auth_rights.map((r) => new Date(r.end_at).getTime()));

  // 当前（天）能下载的数量
  downloadCount[0].total = downloadCount[0].quantity;
  // 每天可下载个数（不包含加油包）
  downloadCount[0].quantity =
    downloadCount[0].quantity -
    tem_rights.reduce((acc, cur) => {
      return acc + cur.quantity;
    }, 0);

  return {
    vips: effect_vips,
    auth_rights,
    downloadCount,
    auth_rights_expire,
    vip_expire: material_vip_expire_at,
    effect_rights,
    subscription: subscription.list,
    rightPkgCodeMap,
  };
}

/**
 * 查询权益包详情信息
 * @param {{
 *  codes?: string,
 *  code?: string,
 * }} query 查询参数
 * @returns {Promise<{list: IRightPackageInfo[], num: number, size: number, total: number}>} 权益包详情列表
 */
export function getRightsPackageList(query) {
  return request({
    url: `/biz/right-package/list`,
    query,
  });
}
